import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProducts } from '../hooks/useProducts';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Office = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const { products: officeProducts, loading } = useProducts('office');
  const [pageContent, setPageContent] = useState(null);
  const productsRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/public/content/office`).then(res => {
      if (res.data) setPageContent(res.data);
    }).catch(() => {});
  }, []);

  // Scroll to products section when arriving with ?category= param
  useEffect(() => {
    if (selectedCategory && productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, []);

  const defaultCategories = [
    { key: 'Monitor Arms', label: { en: 'Monitor Arms', tr: 'Monitör Kolları' }, desc: { en: 'Ergonomic monitor arms', tr: 'Ergonomik monitör kolları' }, image: '' },
    { key: 'TV Stands', label: { en: 'TV Stands', tr: 'TV Standları' }, desc: { en: 'Elegant TV floor stands', tr: 'Şık TV yer standları' }, image: '' },
    { key: 'Mobile TV Stands', label: { en: 'Mobile TV Stands', tr: 'Mobil TV Standları' }, desc: { en: 'Mobile TV stands', tr: 'Mobil TV standları' }, image: '' },
    { key: 'Dyson Stands', label: { en: 'Dyson Stands', tr: 'Dyson Standları' }, desc: { en: 'Dyson vacuum stands', tr: 'Dyson süpürge standları' }, image: '' },
    { key: 'Accessories', label: { en: 'Accessories', tr: 'Aksesuarlar' }, desc: { en: 'Office accessories', tr: 'Ofis aksesuarları' }, image: '' }
  ];

  const categories = (pageContent?.categories || defaultCategories).map(cat => {
    const prods = officeProducts.filter(p => p.category === cat.key).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const label = typeof cat.label === 'object' ? (cat.label[lang] || cat.label.en || '') : cat.label;
    const desc = typeof cat.desc === 'object' ? (cat.desc[lang] || cat.desc.en || '') : cat.desc;
    const image = cat.image || prods[0]?.cardImage || prods[0]?.images?.[0] || '';
    return { key: cat.key, label, desc, image, products: prods };
  });
  
  const filteredProducts = selectedCategory 
    ? officeProducts.filter(p => p.category === selectedCategory).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    : [];

  // Helper to get localized text
  const getText = (field) => {
    if (typeof field === 'object' && field !== null) {
      return field[lang] || field.en || '';
    }
    return field || '';
  };

  useEffect(() => { document.title = 'VIYERO Home & Office | Ergonomik Çözümler'; return () => { document.title = 'VIYERO | Ergonomic Solutions'; }; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-4 md:pb-6 overflow-hidden bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {lang === 'tr' ? (
                <>{getText(pageContent?.hero_title) || 'Konforla Tanışın,'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">{getText(pageContent?.hero_highlight) || 'Veriminizi Artırın'}</span></>
              ) : (
                <>{getText(pageContent?.hero_title) || 'Designed for'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">{getText(pageContent?.hero_highlight) || 'Wellness'}</span></>
              )}
            </h1>
            <p className="text-lg md:text-xl text-gray-600">
              {getText(pageContent?.hero_description) || (lang === 'tr' 
                ? 'Yaşam alanlarınıza sağlık katan, hem bedeninizi hem işinizi destekleyen profesyonel çözümler.'
                : 'Ergonomic solutions that promote health, comfort, and productivity in your home office, bedroom, or living space.')}
            </p>
          </div>

          {/* Hero Image */}
          {!pageContent?.hero_image_hidden && (
            <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
              <img
                src={pageContent?.hero_image || ""}
                alt="Office Hero"
                loading="eager"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
          )}
        </div>
      </section>

      {/* Products Section */}
      <section ref={productsRef} className="py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter - Always visible when a category is selected */}
          {selectedCategory && (
            <>
              <div className="flex items-center mb-8">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedCategory(null)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {lang === 'tr' ? 'Tüm Kategoriler' : 'All Categories'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mb-12 justify-center">
                {categories.map((cat) => (
                  <Button
                    key={cat.key}
                    onClick={() => { setSelectedCategory(cat.key); productsRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' }); }}
                    variant={selectedCategory === cat.key ? 'default' : 'outline'}
                    className={selectedCategory === cat.key 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }
                  >
                    {cat.label}
                  </Button>
                ))}
              </div>
            </>
          )}

          {/* Category Cards - Show when no category selected */}
          {!selectedCategory && (
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              {categories.map((cat) => (
                <div 
                  key={cat.key} 
                  onClick={() => { setSelectedCategory(cat.key); productsRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' }); }}
                  className="cursor-pointer group w-full sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]"
                >
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={cat.image}
                        alt={cat.label}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-4 bg-[#cdcbbf]">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{cat.label}</h3>
                      <p className="text-gray-600 text-sm mb-3">{cat.desc}</p>
                      <div className="flex items-center justify-end">
                        <ArrowRight className="h-5 w-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* Products Grid - Show when category selected */}
          {selectedCategory && (
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              {filteredProducts.map((product) => (
                <div key={product.id} className="w-full sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
                  <ProductCard product={product} />
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div className="w-full text-center py-12">
                  <p className="text-gray-500 text-lg">
                    {lang === 'tr' ? 'Bu kategoride henüz ürün yok.' : 'No products in this category yet.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      {!pageContent?.features_hidden && (
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText(pageContent?.features_title) || (lang === 'tr' ? 'Mükemmel Ergonomi' : 'Ergonomic Excellence')}
            </h2>
            <p className="text-gray-600 text-lg">
              {getText(pageContent?.features_subtitle) || (lang === 'tr' ? 'Sağlığınız ve verimliliğiniz için tasarlandı.' : 'Designed for your health and productivity')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(pageContent?.features || [
              { title: { en: 'Height Adjustable', tr: 'Yüksekliği Ayarlanabilir' }, desc: { en: 'Seamlessly switch between sitting and standing to promote better posture.', tr: 'Daha iyi bir duruşu desteklemek için oturma ve ayakta durma pozisyonları arasında kusursuz geçiş yapın.' } },
              { title: { en: 'Natural Materials', tr: 'Doğal Malzemeler' }, desc: { en: 'Premium wood finishes and sustainable materials for timeless aesthetics.', tr: 'Zamansız bir estetik için birinci sınıf ahşap yüzeyler ve sürdürülebilir materyaller.' } },
              { title: { en: 'Silent Operation', tr: 'Sessiz Çalışma' }, desc: { en: 'Quiet motors and smooth mechanisms that won\'t disturb your focus.', tr: 'Odaklanmanızı bozmayacak sessiz motorlar ve pürüzsüz çalışma mekanizmaları.' } }
            ]).map((feat, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className={`w-12 h-12 bg-gradient-to-br ${idx === 0 ? 'from-blue-500 to-blue-600' : idx === 1 ? 'from-green-500 to-green-600' : 'from-purple-500 to-purple-600'} rounded-lg mb-4`} />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {(typeof feat.title === 'object' ? feat.title[lang] || feat.title.en : feat.title) || ''}
                </h3>
                <p className="text-gray-600">
                  {(typeof feat.desc === 'object' ? feat.desc[lang] || feat.desc.en : feat.desc) || ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <Footer />
    </div>
  );
};

export default Office;
